import React, { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

import {
  generateReferralCode,
  validateReferralCode,
  recordReferralSignup,
  clearStoredReferral,
} from "../utils/referrals";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || "itdesk@afriportb2c.online";

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        const snap = await getDoc(doc(db, "users", firebaseUser.uid));
        if (snap.exists()) {
          const data = snap.data();
          // Ensure user has a referral code
          if (!data.referralCode) {
            const newCode = generateReferralCode(data, firebaseUser.uid);
            try {
              await setDoc(doc(db, "users", firebaseUser.uid), { referralCode: newCode }, { merge: true });
              data.referralCode = newCode;
            } catch (e) {
              console.warn("Could not save auto-generated referral code:", e);
            }
          }
          setProfile(data);
        }
      } else {
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  async function register(email, password, data) {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName: data.fullName });
    const isAdmin = email === ADMIN_EMAIL;
    const role = isAdmin ? "admin" : data.accountType;

    // Generate own referral code
    const ownReferralCode = generateReferralCode(data, cred.user.uid);

    // Check if referred by another user
    let referredBy = null;
    let referredByCode = null;
    let referrerInfo = null;

    if (data.referredByCode?.trim()) {
      const refCheck = await validateReferralCode(data.referredByCode.trim());
      if (refCheck.valid) {
        referredBy = refCheck.referrerId;
        referredByCode = refCheck.code;
        referrerInfo = refCheck;
      }
    }

    const profileData = {
      uid: cred.user.uid,
      email,
      fullName: data.fullName,
      phone: data.phone || "",
      role,
      referralCode: ownReferralCode,
      referralsCount: 0,
      referredBy: referredBy || null,
      referredByCode: referredByCode || null,
      createdAt: serverTimestamp(),
      ...(role === "business" && {
        businessName: data.businessName || "",
        businessAddress: data.businessAddress || "",
        bankName: data.bankName || "",
        accountNumber: data.accountNumber || "",
        accountName: data.accountName || "",
        commissionRate: 10,
        payoutRate: 90,
        businessApproved: false,
      }),
    };

    await setDoc(doc(db, "users", cred.user.uid), profileData);
    setProfile(profileData);

    // If referred, record the referral signup and clear stored referral
    if (referredBy && referrerInfo) {
      await recordReferralSignup({
        referrerId: referredBy,
        referrerName: referrerInfo.referrerName,
        referrerEmail: referrerInfo.referrerEmail,
        refereeId: cred.user.uid,
        refereeName: data.fullName,
        refereeEmail: email,
        code: referredByCode,
      });
      clearStoredReferral();
    }

    return cred;
  }

  async function login(email, password) {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    let snap = await getDoc(doc(db, "users", cred.user.uid));
    let profileData = snap.exists() ? snap.data() : null;
    
    // Check if user should be admin based on email
    const isAdmin = email === ADMIN_EMAIL;
    
    // If profile exists and isn't admin but email matches admin email, update to admin
    if (profileData && !profileData.role && isAdmin) {
      profileData.role = "admin";
      await setDoc(doc(db, "users", cred.user.uid), profileData, { merge: true });
    }
    
    // If profile doesn't exist but email is admin email, create admin profile
    if (!profileData && isAdmin) {
      profileData = {
        uid: cred.user.uid,
        email,
        fullName: cred.user.displayName || "Admin",
        phone: "",
        role: "admin",
        createdAt: serverTimestamp(),
      };
      await setDoc(doc(db, "users", cred.user.uid), profileData);
    }
    
    if (profileData) setProfile(profileData);
    
    // Send login notification email
    try {
      const sendLoginEmail = httpsCallable(functions, "onUserLogin");
      await sendLoginEmail({ email, fullName: profileData?.fullName || cred.user.displayName || "User" });
    } catch (err) {
      console.warn("Could not send login email:", err.message);
    }
    
    return cred;
  }

  async function logout() {
    await signOut(auth);
    setUser(null);
    setProfile(null);
  }

  const isAdmin = profile?.role === "admin";
  const isBusiness = profile?.role === "business";
  const isCustomer = profile?.role === "customer";
  const isWebAdmin = profile?.role === "web_admin";
  const isAccounts = profile?.role === "accounts";
  // Any staff-level access (can see admin dashboard)
  const isStaff = isAdmin || isWebAdmin || isAccounts;

  return (
    <AuthContext.Provider
      value={{ user, profile, loading, register, login, logout, isAdmin, isBusiness, isCustomer, isWebAdmin, isAccounts, isStaff }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
